import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { latestTaskForCode, taskToStatusRecord } from './notification-collection.converters';
import { NotificationState, NotificationStatusRecordState } from './notification-collection.state';

/**
 * The rules the device records travel statuses by. They predict the tasks the backend creates for
 * the same submit, so that what the device shows before the next NDP load — or while offline —
 * already matches what that load will bring:
 *
 * - ETA: start = the DISP start (the originally planned start), end = the announced arrival. Unlike
 *   every other status, the end is not when the status ended but the updated start.
 * - DURA, written together with ETA: start = the announced arrival, end = that plus the DISP duration.
 *   Where the backend is configured not to create a DURA task, the device still keeps this DURA,
 *   re-derived from the ETA on every load (see `reconcileLoadedStatuses`), so DURA is always the
 *   window to show once an arrival was announced. A duration change rewrites its end.
 * - TRAV: start = end = the time the user entered; the end moves to the OSR start once OSR is sent.
 * - OSR: start = end = the time the user entered.
 *
 * Every other status the travel-status dialog submits keeps the window the dialog computed.
 *
 * All times are truncated to the minute, the resolution `taskToStatusRecord` gives the backend's
 * tasks, so a recorded status and the task it predicts compare equal.
 */

/** What one travel-status submit — or a duration change, which rewrites DURA — asks the device to record. */
export type TravelStatusSubmission = Readonly<{
    statusCode: string;
    /** The time the user entered — the one sent to the backend — as an ISO timestamp. */
    start: string;
    /**
     * The end the dialog computed, as an ISO timestamp. Only kept for a duration change and for
     * statuses without a rule.
     */
    end: string;
}>;

function toMinute(time: string | moment.Moment): moment.Moment {
    return moment(time).startOf('minute');
}

function record(statusCode: string, start: moment.Moment, end: moment.Moment): NotificationStatusRecordState {
    return { statusCode, startTime: start.toISOString(), endTime: end.toISOString() };
}

/**
 * The DISP window: the backend's DISP task, which every dispatched notification has, else the DISP
 * status the device recorded from it earlier.
 *
 * @param entry
 */
function dispatchWindowFor(entry: NotificationState): NotificationStatusRecordState | undefined {
    const task = latestTaskForCode(entry.tasks, NOTIFICATION_STATUS_CODE.DISP);
    if (task) {
        return taskToStatusRecord(task);
    }
    return entry.status.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.DISP);
}

/**
 * The DURA an announced arrival implies: from the arrival, for as long as DISP was planned. Without
 * a DISP window — which a well-formed notification never lacks — it collapses onto the arrival.
 *
 * @param entry the notification the arrival was announced for
 * @param arrival the announced arrival, i.e. the ETA's end
 */
export function durationRecordForArrival(
    entry: NotificationState,
    arrival: string | moment.Moment,
): NotificationStatusRecordState {
    const dispatchWindow = dispatchWindowFor(entry);
    const start = toMinute(arrival);
    const plannedDurationInMinutes = dispatchWindow
        ? toMinute(dispatchWindow.endTime).diff(toMinute(dispatchWindow.startTime), 'minutes')
        : 0;
    return record(NOTIFICATION_STATUS_CODE.DURA, start, start.clone().add(plannedDurationInMinutes, 'minutes'));
}

/**
 * The status records one travel-status submit produces, replacing any the entry already carries for
 * the same codes. Empty when there is nothing to write.
 *
 * Without a DISP window — which a well-formed notification never lacks — ETA starts at the announced
 * arrival and DURA collapses to it, rather than the reducer having to fail.
 *
 * @param entry the notification the status is recorded on
 * @param submission what the user submitted
 */
export function statusRecordsForTravelStatus(
    entry: NotificationState,
    submission: TravelStatusSubmission,
): NotificationStatusRecordState[] {
    const time = toMinute(submission.start);

    switch (submission.statusCode) {
        case NOTIFICATION_STATUS_CODE.ETA: {
            const dispatchWindow = dispatchWindowFor(entry);
            const plannedStart = dispatchWindow ? toMinute(dispatchWindow.startTime) : time;
            return [record(NOTIFICATION_STATUS_CODE.ETA, plannedStart, time), durationRecordForArrival(entry, time)];
        }
        case NOTIFICATION_STATUS_CODE.DURA:
            // A duration change: the start stays, the user chose the end.
            return [record(NOTIFICATION_STATUS_CODE.DURA, time, toMinute(submission.end))];
        case NOTIFICATION_STATUS_CODE.TRAV:
            return [record(NOTIFICATION_STATUS_CODE.TRAV, time, time)];
        case NOTIFICATION_STATUS_CODE.OSR: {
            const travel = entry.status.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.TRAV);
            const closedTravel = travel ? [record(travel.statusCode, toMinute(travel.startTime), time)] : [];
            return [...closedTravel, record(NOTIFICATION_STATUS_CODE.OSR, time, time)];
        }
        default:
            // No rule for this status: recorded once, with the dialog's window, as before.
            return entry.status.some((status) => status.statusCode === submission.statusCode)
                ? []
                : [{ statusCode: submission.statusCode, startTime: submission.start, endTime: submission.end }];
    }
}

/**
 * `statuses` with each of `records` in place of the status of the same code, appended where the
 * entry has none yet. Order is kept, so a replaced status stays where it was.
 *
 * @param statuses the statuses the entry carries
 * @param records the statuses to write
 */
export function upsertStatusRecords(
    statuses: readonly NotificationStatusRecordState[],
    records: readonly NotificationStatusRecordState[],
): NotificationStatusRecordState[] {
    return records.reduce<NotificationStatusRecordState[]>(
        (current, written) => {
            const index = current.findIndex((status) => status.statusCode === written.statusCode);
            return index === -1 ? [...current, written] : current.map((status, i) => (i === index ? written : status));
        },
        [...statuses],
    );
}

/**
 * Whether two records of the same status cover the same window, to the minute. Records persisted
 * before the minute truncation was applied everywhere are compared on equal terms this way.
 *
 * @param a
 * @param b
 */
export function isSameStatusWindow(a: NotificationStatusRecordState, b: NotificationStatusRecordState): boolean {
    return toMinute(a.startTime).isSame(toMinute(b.startTime)) && toMinute(a.endTime).isSame(toMinute(b.endTime));
}
